import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Container, Image, Form, Spinner } from "react-bootstrap";
import ReCAPTCHA from "react-google-recaptcha";

import "bootstrap/dist/css/bootstrap.min.css";
import "./signin-email.css";

import responsivebg from "../../assets/images/responsive-bg.png";

import backarrow from "../../assets/images/backarrow.jpg";
import logo from "../../assets/images/MTDlogo.png";

import { loginEmailOTP } from "../../actions/auth";
import IconButton from "../userflow/components/card/IconButton";
import { useCookies } from "../../hooks/useCookies";
import { useRateLimit } from "../../hooks/useRateLimit";

// Constants
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const SignInEmail = () => {
  const recaptchaRef = useRef(null);
  const navigate = useNavigate();
  const { setCookie } = useCookies();
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  // Rate limiting hook
  const {
    isRateLimited,
    timeRemaining,
    handleRateLimitError,
    formatTimeRemaining,
    getRateLimitMessage
  } = useRateLimit('signin-email');

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    setErrorMessage('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (loading || isRateLimited) return;

    if (!email || !EMAIL_REGEX.test(email)) {
      setErrorMessage("Please enter a valid email address.");
      return;
    }

    setLoading(true);
    setErrorMessage("");

    try {
      recaptchaRef.current.reset();
      const token = await recaptchaRef.current.executeAsync();

      const response = await loginEmailOTP({
        email,
        recaptchaToken: token
      });

      if (response.response) {
        setLoading(false);

        // Check for rate limit error
        if (handleRateLimitError(response)) {
          setErrorMessage(getRateLimitMessage());
        } else {
          setErrorMessage(response.response?.data?.message || response.message || "Failed to send code");
        }
        return;
      }

      setLoading(false);
      setCookie("email", email, 7);
      navigate("/signinemailotp");
      setEmail("");
    } catch (error) {
      setLoading(false);
      setErrorMessage(error.response?.data?.message || error.message || "An unexpected error occurred");
    }
  };

  return (
    <div className="signin-emailverify-container">
      <div className="signin-emailverify-bg">
        <Image className="responsive-bg" src={responsivebg}></Image>
      </div>
      <Container className="signin-emailverify-main">
        <Container className="signin-emailverify-box">
          <Container className="logo-progressbar3">
            <Container className="logo-arrow3">
              <Image
                src={backarrow}
                className="backarrow"
                onClick={() => window.history.back()}
              />
              <Image src={logo} alt="Logo" className="logo" />
            </Container>
          </Container>

          <Container className="signin-emailverify-text">
            <IconButton type="envelope" onClick={e => e.stopPropagation()} />
          </Container>
          <Form onSubmit={handleSubmit} className="signin-emailverify-form">
            <Container className="signin-emailverify-content">
              <Form.Group controlId="emailInput" className="signin-emailverify-form-group">
                <Form.Label htmlFor="emailInput" className="signin-emailverify-lable">
                  Enter your email
                </Form.Label>

                <Form.Control
                  id="emailInput"
                  className={`signin-emailverify-input ${errorMessage ? "error" : ""}`}
                  type="email"
                  placeholder="example@example.com"
                  autoComplete="off"
                  autoSave="off"
                  spellCheck="false"
                  disabled={loading}
                  value={email}
                  onChange={handleEmailChange}
                  onCopy={(e) => e.preventDefault()}
                  onPaste={(e) => e.preventDefault()}
                  onCut={(e) => e.preventDefault()}
                  aria-describedby={errorMessage ? "emailError" : undefined}
                  aria-invalid={!!errorMessage}
                  aria-required="true"
                />

                {errorMessage && (
                  <Form.Text id="emailError" className="text-danger error-message" role="alert">
                    {errorMessage}
                    {isRateLimited && timeRemaining > 0 && (
                      <div style={{ marginTop: "5px", fontWeight: "bold" }}>
                        Time remaining: {formatTimeRemaining(timeRemaining)}
                      </div>
                    )}
                  </Form.Text>
                )}
              </Form.Group>
              <ReCAPTCHA
                ref={recaptchaRef}
                sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                size="invisible"
                badge="bottomright"
                style={{
                  visibility: 'hidden',
                  position: 'absolute',
                  left: '-9999px'
                }}
                onErrored={() => console.log("reCAPTCHA errored")}
                onExpired={() => console.log("reCAPTCHA expired")}
              />
              <button
                type="submit"
                className="global-next-btn"
                disabled={loading || isRateLimited}
                title={isRateLimited ? "Please wait before trying again" : ""}
              >
                {loading ? (
                  <Spinner animation="border" size="sm" role="status" />
                ) : isRateLimited ? (
                  `Try again in ${formatTimeRemaining(timeRemaining)}`
                ) : (
                  "Next"
                )}
              </button>
            </Container>
          </Form>
        </Container>
      </Container>
    </div>
  );
};
